import React from 'react';
import {
  Target, Lock, Timer, Ban, RotateCcw, Split, Flag,
  CloudRain, CalendarClock, CalendarX, Trophy, EyeOff,
} from 'lucide-react';
import gafferIcon from '../../assets/gaffer-head-icon.png';
import './rules.css';

/**
 * The rules, in full, including the parts nobody reads until they need them.
 *
 * The page this replaced was six short paragraphs and three strategy tips. It described a
 * game where you pick a team and win or lose, which is true and is also the half of the
 * product that never generates a question. Every message the game actually produces comes
 * from the other half: everybody went out at once and now we are all back in, a match was
 * postponed and my week vanished, I forgot to pick and something picked for me, the season
 * ran out with three of us left. A player meeting one of those for the first time has
 * nowhere to look, and a manager fielding it has nothing to point at.
 *
 * So the shape here is **the game, then what happens when the game gets strange**. The
 * ordinary rules are four lines at the top and then never repeated; the rest of the page is
 * the edge cases, each under a heading somebody would actually search for.
 *
 * Three rules held to throughout.
 *
 * **Say the outcome, not the mechanism.** "Every pick made that round is handed back" is
 * something a player can act on. Which service voids the week is not, and the moment this
 * page starts describing internals it starts going out of date silently.
 *
 * **The Gaffer's section says what he is and refuses to say how he picks.** See
 * `GafferToggle` - that rule predates this page and is not this page's to relax. Working him
 * out is the game; publishing the answer deletes it. He is also never called a bot here.
 *
 * **A jump list, not an accordion.** This is reference material somebody arrives at mid-
 * argument, so every answer is on the page, in the DOM, findable with a browser search.
 * Collapsing the sections would hide exactly the paragraph the search was for.
 */
function RulesPage() {
  return (
    <div className="page-container fade-in">
    <div className="rp">

      <header className="rp-hero">
        <span className="rp-eyebrow">The rules · 2026/27</span>
        <h1 className="rp-title">How it all works.</h1>
        <p className="rp-lede">
          The game takes four lines. The rest of this page is what happens when a
          weekend does not go to plan.
        </p>
      </header>

      <nav className="rp-jump" aria-label="On this page">
        <JumpLink to="basics">The basics</JumpLink>
        <JumpLink to="round">A round, start to finish</JumpLink>
        <JumpLink to="deadline">Missing the deadline</JumpLink>
        <JumpLink to="teams">Running out of teams</JumpLink>
        <JumpLink to="rollover">Everybody out at once</JumpLink>
        <JumpLink to="split">The last two</JumpLink>
        <JumpLink to="season-end">When the fixtures run out</JumpLink>
        <JumpLink to="calendar">Full rounds only</JumpLink>
        <JumpLink to="void">Postponed matches</JumpLink>
        <JumpLink to="partial">Across the weekend</JumpLink>
        <JumpLink to="gaffer">The Gaffer</JumpLink>
        <JumpLink to="leagues">Mini leagues</JumpLink>
        <JumpLink to="hidden">What nobody can see</JumpLink>
      </nav>

      {/* ---------------------------------------------------------------- basics */}
      <Section id="basics" title="The basics">
        <p className="rp-body">
          Last Man Standing is one decision a week, repeated until there is one of you left.
        </p>
        <ol className="rp-rules">
          <Rule n="1" icon={Target} title="One team a week">
            Every round has a set of fixtures. Pick the one team from them you think will
            win.
          </Rule>
          <Rule n="2" icon={Trophy} title="Win and you are through">
            A win keeps you in. A defeat puts you out &mdash; and so does a draw. There is no
            credit for a point.
          </Rule>
          <Rule n="3" icon={Lock} title="Each team once">
            Spend Arsenal in September and Arsenal is gone. You cannot pick the same team
            twice in one run, which is what turns a weekly guess into a season-long plan.
          </Rule>
          <Rule n="4" icon={Flag} title="Last one standing takes it">
            When one player is left, the game is over and it is theirs.
          </Rule>
        </ol>
      </Section>

      {/* ---------------------------------------------------------------- a round */}
      <Section id="round" title="A round, start to finish">
        <p className="rp-body">
          Four things happen to every round, in this order, and most of the questions on this
          page are really questions about which of the four you are in.
        </p>

        <ol className="rp-steps">
          <Step icon={CalendarClock} label="The fixtures go up">
            The round&rsquo;s matches appear on your game and picking opens. Teams you have
            already used are shown but cannot be chosen.
          </Step>
          <Step icon={Timer} label="The deadline">
            <strong>One hour before the first kick-off of the round</strong> &mdash; not
            before your own match, before the earliest one in the whole round. Picking shuts,
            everybody&rsquo;s pick becomes visible at once, and nothing can be changed after
            it.
          </Step>
          <Step icon={Ban} label="The results come in">
            You go out the moment your team&rsquo;s result lands. If your team lost on the
            Saturday you are out on the Saturday, whatever else is still to be played and
            whoever is still to kick off.
          </Step>
          <Step icon={Flag} label="The round closes">
            Once every match in the round has a result, the round is settled: anyone with no
            pick at all goes out, and only then does the game ask whether it has a winner.
          </Step>
        </ol>

        <Aside>
          The gap between the last two is deliberate. A Saturday result can knock you out; it
          can never crown anybody, because somebody else&rsquo;s team has not played yet.
        </Aside>
      </Section>

      {/* ---------------------------------------------------------------- deadline */}
      <Section id="deadline" title="Missing the deadline">
        <p className="rp-body">
          Forget to pick and the game picks for you. You are given a team at random from that
          round&rsquo;s fixtures &mdash; one you have not already used &mdash; and it counts
          exactly as though you had chosen it yourself. It wins, you are through. It draws,
          you are out. It is spent either way and you cannot use it again.
        </p>
        <p className="rp-body">
          You are told it was an auto-pick, and so is everybody else. Nobody is quietly
          eliminated for being busy on a Friday night, but nobody gets a better team out of it
          either.
        </p>
      </Section>

      {/* ---------------------------------------------------------------- teams */}
      <Section id="teams" title="Running out of teams">
        <p className="rp-body">
          Since each team can only be spent once, a long run eventually leaves you short. If a
          round arrives and there is nothing left that you are allowed to pick, that round is a
          no-pick, and a no-pick is an elimination like any other &mdash; it is recorded as one
          and it shows on the board as one.
        </p>
        <p className="rp-body">
          This is the reason the pick screen is worth more than the fixture list. Use the
          planner to look five rounds ahead before you spend a big side on a home banker in
          October &mdash; and read <a href="#calendar">every round is a full round</a>, because
          a team you are holding back does not come up as often as the real fixture list
          suggests.
        </p>
      </Section>

      {/* ---------------------------------------------------------------- rollover */}
      <Section id="rollover" title="Everybody out at once" icon={RotateCcw}>
        <p className="rp-body">
          It happens, and more often than you would think: a weekend of upsets takes out every
          player still standing. Nobody has won, because nobody survived, so the game does not
          end.
        </p>
        <p className="rp-body">It <strong>rolls over</strong>, which means all of this at once:</p>
        <ul className="rp-list">
          <li>Everyone who was in the game comes straight back in &mdash; the whole roster,
            not just that round&rsquo;s casualties.</li>
          <li>Every team is handed back. You start again with the full set, including the ones
            you had already spent.</li>
          <li>The game carries on from the next round, with the same players and the same
            pot.</li>
          <li>Your record stays on the board. A rollover wipes the slate for picking; it does
            not pretend the rounds before it never happened.</li>
        </ul>
        <Aside>
          A rollover needs somewhere to go. If everybody goes out on the last round of the
          season there is no next round to roll into &mdash; see <a href="#season-end">when
          the fixtures run out</a>.
        </Aside>
      </Section>

      {/* ---------------------------------------------------------------- split */}
      <Section id="split" title="The last two" icon={Split}>
        <p className="rp-body">
          Down to two players, either of you can offer to split rather than play it out. The
          other accepts or turns it down.
        </p>
        <ul className="rp-list">
          <li><strong>Accepted</strong> &mdash; the game ends there and then as a shared win.
            Both of you are winners of it, and both are recorded that way.</li>
          <li><strong>Turned down</strong> &mdash; you play on, and no further offer can be
            made in that same round. Next round, either of you can ask again.</li>
        </ul>
        <p className="rp-body">
          An offer can only be made <em>between</em> rounds, never while a round&rsquo;s
          results are still coming in. Being the last two on a Saturday night with a Sunday
          fixture outstanding is not the same thing as being the last two, and a deal struck in
          that gap would end a game somebody was still winning.
        </p>
        <Aside>
          A split settles the game. What the two of you do about an actual pot is between you
          &mdash; the site keeps the result, not the money.
        </Aside>
      </Section>

      {/* ---------------------------------------------------------------- season end */}
      <Section id="season-end" title="When the fixtures run out" icon={Flag}>
        <p className="rp-body">
          Every competition has a last round, and it is marked as the last one rather than
          worked out from a number &mdash; so a rearranged fixture list cannot end a game
          early or drag it on past the end.
        </p>
        <p className="rp-body">On that final round, one of three things happens:</p>
        <div className="rp-cases">
          <Case verdict="One player left" tone="win">
            The ordinary ending. They have won it.
          </Case>
          <Case verdict="Two or more still standing" tone="split">
            There is nothing left to separate you, so the game ends as a shared win between
            all of you &mdash; the same result as an agreed split, reached by running out of
            season instead.
          </Case>
          <Case verdict="Everybody out" tone="none">
            No rollover, because there is no round left to roll into. The game closes with no
            winner. It is a real result rather than a fault, and it is recorded as one.
          </Case>
        </div>
        <p className="rp-body">
          A game started late in a season simply has fewer rounds to work with, and reaches
          that same last round sooner.
        </p>
      </Section>

      {/* -------------------------------------------------------------- calendar */}
      <Section id="calendar" title="Every round is a full round" icon={CalendarX}>
        <p className="rp-body">
          A round is only ever published when <strong>every team in the competition has a
          fixture in it</strong>. That is a rule, not an aim. There is no such thing on here as
          a round where half the field is sitting out.
        </p>
        <p className="rp-body">
          Real football does not oblige, and that includes the Premier League. Cup weekends,
          European nights, international breaks and part-played midweeks all leave match weeks
          where only a handful of clubs are out. Not every one of those weeks can be made into
          a sheet, and where one cannot, there is no round: the week is skipped and the game
          moves on to the next full programme. It happens a few times a season, every season,
          and it is not something we can prevent, because we do not set the fixture list.
        </p>

        <h3 className="rp-h3">The call is ours, and it is final</h3>
        <p className="rp-body">
          Fixture sheets are put together by us. Whether a given match week can produce a full
          sheet &mdash; and therefore whether it becomes a round at all &mdash; is our decision,
          and it is <strong>final</strong>. It is not open to appeal and it is not a vote.
        </p>
        <p className="rp-body">
          Not because we fancy the last word. Somebody has to publish a round by the end of the
          week, and a fixture list that moved on the Thursday cannot wait while a group argues
          about whether eight matches is enough. Drawing the line at <em>all of them</em> is
          what stops that argument existing: there is nothing to dispute, because there is no
          judgement call about how short is too short.
        </p>

        <h3 className="rp-h3">Why skipping is the fairer answer</h3>
        <p className="rp-body">
          Run a round on a six-match week and the game stops being about football. Whether you
          can pick at all comes down to whether you happen to still hold one of the twelve clubs
          the fixture computer put out that week &mdash; and so does whether you survive it. Two
          players with identical judgement get opposite weeks for a reason neither of them
          chose, and the one who is stuck has no move to make about it.
        </p>
        <p className="rp-body">
          A skipped week costs everybody exactly the same thing: nothing. That is the whole
          argument, and it is why the rule has no exceptions in it.
        </p>

        <h3 className="rp-h3">What the rule costs</h3>
        <p className="rp-body">
          A team playing in a skipped week does not appear in the game that week, and there is
          no giving it back to them. So across a season sides turn up an uneven number of times
          &mdash; one might feature in nine rounds and another in seven &mdash; and the team you
          were saving for March may come up less often than you were counting on.
        </p>
        <p className="rp-body">
          We would rather say that out loud than not. It is a real cost and we would pay it
          again: a season where Brentford come up seven times and Fulham nine is a fairer game
          than one round in November decided by the fixture computer.
        </p>
        <p className="rp-body">
          What is fixed either way is that everybody picks from the same sheet, at the same
          deadline, with the same information. Nobody gets a round you did not get.
        </p>

        <h3 className="rp-h3">Once a round is published</h3>
        <p className="rp-body">
          A full sheet can still lose a match afterwards &mdash; a postponement on the Thursday,
          a game abandoned at half time. One or two of those is ordinary and it is a different
          question with its own answer: see <a href="#void">postponed and abandoned
          matches</a>.
        </p>
        <p className="rp-body">
          A published round can also still change up until it locks, if the real fixtures move
          underneath it. Same call, same rule: full sheet or no round.
        </p>
        <Aside>
          Worth knowing when you plan ahead: the planner shows you the rounds we have actually
          published, not every week between now and May.
        </Aside>
      </Section>

      {/* ---------------------------------------------------------------- void */}
      <Section id="void" title="Postponed and abandoned matches" icon={CloudRain}>
        <p className="rp-body">
          A match that is called off, abandoned or otherwise never resulted is <strong>voided</strong>.
          A void does not automatically wreck the round for everybody &mdash; the question is
          asked separately for each game, when that game&rsquo;s round closes, and the answer
          depends only on the players still in it.
        </p>
        <div className="rp-cases">
          <Case verdict="Somebody still in it backed a voided match" tone="void">
            The whole round is voided for that game. Nobody goes out, everyone carries
            forward, and <strong>every</strong> team picked that round is handed back &mdash;
            not only the teams from the match that was called off. The game moves on to the
            next round with everybody in it.
          </Case>
          <Case verdict="Nobody still in it did" tone="win">
            The round is settled exactly as normal and the void is ignored. A postponement in
            a match none of you touched is somebody else&rsquo;s problem.
          </Case>
        </div>
        <p className="rp-body">
          Players already eliminated in earlier rounds do not count towards that question, and
          neither do other people&rsquo;s games. The same postponed match can void the round in
          one game and pass unnoticed in the next.
        </p>
      </Section>

      {/* ---------------------------------------------------------------- partial */}
      <Section id="partial" title="One round, spread across a weekend" icon={CalendarClock}>
        <p className="rp-body">
          A round is rarely ninety minutes on a Saturday. Matches are spread from Friday night
          to Monday, and a round is only finished when every match in it has been resulted or
          voided.
        </p>
        <ul className="rp-list">
          <li><strong>Eliminations are immediate.</strong> Your team plays Friday and loses,
            you are out on Friday. You do not wait for the rest of the round, and neither does
            anybody else waiting to see whether you survived.</li>
          <li><strong>Endings wait.</strong> Winners, splits, rollovers and no-winner endings
            are only decided once the round is complete. A game cannot be won on a Saturday
            while a Monday night match still has somebody&rsquo;s season riding on it.</li>
          <li><strong>The deadline is one deadline.</strong> It is set against the first
            kick-off of the round, so a Monday night fixture is picked at the same time as a
            Friday one. Nobody gets three extra days of team news.</li>
        </ul>
        <p className="rp-body">
          So mid-round the board can honestly show four players out and no result yet. That is
          the round working, not the round stuck.
        </p>
      </Section>

      {/* ---------------------------------------------------------------- gaffer */}
      <section className="rp-section rp-gaffer" id="gaffer">
        <img className="rp-gaffer-face" src={gafferIcon} alt="" aria-hidden="true" />
        <div className="rp-gaffer-copy">
          <h2 className="rp-h2">Beat The Gaffer</h2>
          <p className="rp-body">
            The Gaffer is an optional extra player. Switch him on and he joins your game as a
            rival: same rules, same fixtures, one team a week, each team once, and he goes out
            when his team loses like everybody else.
          </p>
          <p className="rp-body">
            He picks every single round without fail, which makes him a body in a small game
            and a nuisance in a big one. He gets no inside track: no odds, no team news, no
            sight of anyone&rsquo;s pick before the deadline &mdash; the same deadline applies
            to him. And he will have plenty to say about how your weekend went.
          </p>
          <p className="rp-body rp-body-quiet">
            How he chooses is not published, and that is the whole point of him. Working the
            Big Dog out is half the game; if we printed it here there would be nothing left to
            beat.
          </p>
          <dl className="rp-gaffer-facts">
            <Fact label="Who turns him on">
              The manager, per game. In a mini league it is a league setting, and changing it
              takes effect from the next game rather than the one being played.
            </Fact>
            <Fact label="He can win">
              He is a real competitor and can be the last one standing. If you are playing for
              a pot, decide that before you let him in &mdash; somebody has to explain where
              the money went.
            </Fact>
            <Fact label="He cannot win a mini league">
              He plays the games and can take one, but he is left out of the league table and
              can never be named league winner.
            </Fact>
          </dl>
        </div>
      </section>

      {/* ---------------------------------------------------------------- leagues */}
      <Section id="leagues" title="Mini leagues" icon={Trophy}>
        <p className="rp-body">
          A single game ends the moment somebody wins it, which for most groups is the wrong
          shape: half of you are out by November and there is nothing to play for until next
          season. A mini league is the same group playing all year.
        </p>

        <h3 className="rp-h3">It runs itself</h3>
        <p className="rp-body">
          A league runs one or more competitions &mdash; the Premier League, the Championship,
          Serie A, La Liga, as many as you like. Whenever a game inside it finishes, however it
          finished, the next one starts automatically from the following round with the same
          members. Getting knocked out in a fortnight means you are back in a fortnight.
        </p>

        <h3 className="rp-h3">The table</h3>
        <p className="rp-body">
          Points carry across every game the league has ever played, so a season is judged on
          more than one lucky run.
        </p>
        <dl className="rp-points">
          <div className="rp-point">
            <dt>1 point</dt>
            <dd>every round you survive &mdash; a correct pick</dd>
          </div>
          <div className="rp-point">
            <dt>10 points</dt>
            <dd>winning a game outright</dd>
          </div>
          <div className="rp-point">
            <dt>4 points</dt>
            <dd>a shared win &mdash; each of you gets the four, it is never divided</dd>
          </div>
        </dl>
        <p className="rp-body rp-body-quiet">
          Which is to say: surviving is worth having, but winning one outright is worth ten
          weeks of it.
        </p>

        <h3 className="rp-h3">Winning the league</h3>
        <p className="rp-body">
          The league is decided the moment its last competition finishes &mdash; not overnight,
          not the following week. Everybody level at the top wins it together, exactly as two
          players left standing share a game. If nobody has scored at all, nobody wins it; that
          is a result rather than a gap, and the league will not invent a champion to fill it.
        </p>
        <p className="rp-body">
          A finished league stays finished. No further competitions can be added to it and the
          winners never change.
        </p>

        <h3 className="rp-h3">Getting in</h3>
        <ul className="rp-list">
          <li><strong>Before anything has started</strong>, the league&rsquo;s code lets anyone
            straight in. Nothing has been missed, so there is nothing to ask about.</li>
          <li><strong>Once the first game is under way</strong>, the code raises a request
            instead and the manager decides. A season already running is not something to walk
            into unannounced.</li>
          <li><strong>An invitation from the manager always walks straight in</strong>, at any
            point. It is their yes, already given &mdash; asking them again would be asking the
            same person the same question twice.</li>
        </ul>
        <p className="rp-body">
          Joining a league&rsquo;s game by its code joins you to the league itself. There is no
          halfway house: you are in the league or you are in a standalone game.
        </p>
        <Aside>
          Leave a league and your points go with you. Rejoin and they come back &mdash; the
          table is worked out from what was actually played, not from a running total anybody
          can reset.
        </Aside>
      </Section>

      {/* ---------------------------------------------------------------- hidden */}
      <Section id="hidden" title="What nobody can see" icon={EyeOff}>
        <ul className="rp-list">
          <li><strong>Your pick, until the deadline.</strong> Not the other players, not the
            manager, not the Gaffer. It is withheld by the game rather than merely left off the
            screen, so there is no way round it.</li>
          <li><strong>Nothing that gives it away either.</strong> The count of teams somebody
            has left is worked out only from rounds everyone can already see, so it cannot
            move when a pick is made and cannot be used to guess one.</li>
          <li><strong>The moment the round locks, everything opens.</strong> Every pick, all at
            once, for everybody. From then on the round is a matter of public record and stays
            on the board for the rest of the season.</li>
        </ul>
      </Section>

      <footer className="rp-foot">
        <p className="rp-body">
          Anything here you would settle differently in your own group &mdash; the pot, what a
          split is worth, who gets the Gaffer &mdash; settle it in your group. These are the
          rules the game enforces.
        </p>
      </footer>

    </div>
    </div>
  );
}

function JumpLink({ to, children }) {
  return <a className="rp-jump-link" href={`#${to}`}>{children}</a>;
}

function Section({ id, title, icon: Icon, children }) {
  return (
    <section className="rp-section" id={id}>
      <h2 className="rp-h2">
        {Icon && <Icon className="rp-h2-icon" size={22} aria-hidden="true" />}
        {title}
      </h2>
      {children}
    </section>
  );
}

function Rule({ n, icon: Icon, title, children }) {
  return (
    <li className="rp-rule">
      <span className="rp-rule-n" aria-hidden="true">{n}</span>
      <div className="rp-rule-copy">
        <h3><Icon size={18} aria-hidden="true" />{title}</h3>
        <p>{children}</p>
      </div>
    </li>
  );
}

function Step({ icon: Icon, label, children }) {
  return (
    <li className="rp-step">
      <span className="rp-step-icon" aria-hidden="true"><Icon size={20} /></span>
      <div>
        <h3>{label}</h3>
        <p>{children}</p>
      </div>
    </li>
  );
}

/**
 * The "and this is the bit that catches people" note. Deliberately not a warning colour:
 * none of these are mistakes the reader has made, they are consequences of a rule stated
 * two paragraphs above.
 */
function Aside({ children }) {
  return <p className="rp-aside">{children}</p>;
}

function Case({ verdict, tone, children }) {
  return (
    <div className={`rp-case rp-case-${tone}`}>
      <h3>{verdict}</h3>
      <p>{children}</p>
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div className="rp-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export default RulesPage;
